import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SaveSearchFilterComponent } from "@components/save-search-filter/save-search-filter.component";
import { SavedSearchFilterTabsComponent } from "@components/saved-search-filter-tabs/saved-search-filter-tabs.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import _ from "lodash";
import { Observable, forkJoin, of } from "rxjs";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { INamedSearchFilter } from "~app/common/components/save-search-filter/model/INamedSearchFilter";
import { Anskaffningsformer, ExternArbetsorderStatus } from "~app/common/models/constants";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { FormatService } from "~app/common/services/format.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ITreeSelectionItem } from "~app/features/produktion/produktinstruktioner/models/ProduktdefinitionModels";
import { ICKLeverantor } from "../../models/ICKLeverantor";
import { IExternArbetsorderSearchFilter } from "../../models/IExternArbetsorderSearchFilter";
import { IProduktionsomrade } from "../../models/IProduktionsomrade";
import { ITransportforetag } from "../../models/ITransportforetag";
import { ExternArbetsorderService } from "../../services/externArbetsorder.service";

const DEFAULT_MODEL: any = {
    transportavslutad: undefined,
    transportavslutadFormValue: null,
    uppdateratViolFrom: null,
    uppdateratViolTom: null,
    giltigDatum: null,
    transporttilldeladFormValue: null,
    transporttilldelad: undefined,
    grotFormValue: null,
    grot: undefined,
    avtalsobjekt: "",
    transportforetag: [],
    uppdragsgivare: [],
    produktionsomraden:[],
    status: [],
    anskaffningsform: undefined,
    isDirtyValue: null,
    isDirty: null
};

@Component({
    selector: "rask-extern-arbetsorder-search-panel",
    templateUrl: "./extern-arbetsorder-search-panel.component.html",
    styleUrls: ["./extern-arbetsorder-search-panel.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SavedSearchFilterTabsComponent,
        FormsModule,
        ReactiveFormsModule,
        MultiSelectorComponent,
        DatepickerComponent,
        SaveSearchFilterComponent,
        SelectorComponent,
        RadiobuttonGroupComponent,
        InputComponent,
        SubmitButtonComponent,
        ButtonComponent
    ]
})
export class ExternArbetsorderSearchPanelComponent implements OnInit {
    readonly webbInstallningarRecent = "externarbetsorder.searchparams";
    readonly webbInstallningarSparadeKey = "search.externarbetsorder.saved.filters";

    protected activetypes: IRadiobuttonItem[] = [
        { key: "true",
            value: "Ja" },
        { key: "false",
            value: "Nej" },
        { key: "",
            value: "Alla" }
    ];
    protected form: UntypedFormGroup;
    protected model = _.cloneDeep(DEFAULT_MODEL);
    protected produktionsomraden: ITreeSelectionItem[];
    protected transportforetagList: Observable<ITransportforetag[]>;
    protected uppdragsgivarList: Observable<ICKLeverantor[]>;
    protected valbaraAnskaffningsformer = Anskaffningsformer.ExternArbetsorderAnskaffningsformer;
    protected valbaraStatusar: Observable<any>;

    private readonly externArbetsorderService = inject(ExternArbetsorderService);
    private readonly formBinderService = inject(FormBinderService);
    private readonly formatService = inject(FormatService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    ngOnInit(): void {
        this.valbaraStatusar = of(ExternArbetsorderStatus.StatusarForSokning);
        forkJoin([
            this.externArbetsorderService.getAllTransportforetag(),
            this.externArbetsorderService.getAllUppdragsgivare(),
            this.externArbetsorderService.getAllProduktionsomraden()
        ]).subscribe(([
            transportforetag,
            uppdragsgivare,
            produktionsomraden
        ]) => {
            this.transportforetagList = of(transportforetag);
            this.uppdragsgivarList = of(uppdragsgivare.ckLeverantorer);
            this.produktionsomraden = this.getProduktionsomradenSelectionItems(produktionsomraden);
            _.assign(this.model, _.cloneDeep(this.webinstallningarService.get(this.webbInstallningarRecent)));
            this.model.transportAvslutad = this.formatService.stringToBoolean(this.model.transportavslutadFormValue);
            this.model.transporttilldelad = this.formatService.stringToBoolean(this.model.transporttilldeladFormValue);
            this.model.grot = this.formatService.stringToBoolean(this.model.grotFormValue);
            this.model.isDirty = this.formatService.stringToBoolean(this.model.isDirtyValue);
            this.form = this.formBinderService.CreateGroup(this.model);
            this.onSearch();
        });
    }

    onFilterChanged(filter: INamedSearchFilter) {
        this.model = _.cloneDeep(filter.filter);
        this.formBinderService.reBindForm(this.form, this.model);
    }

    statusSearchFn = (val: string, item: any) => item.value?.toUpperCase().includes(val?.toUpperCase()) ?? false;

    statusViewFormatter = (item: any) => item?.value ?? item;

    statusOutputFormatter = (item: any) => item?.key ?? item;

    transportforetagSearchFn = (val: string, item: ITransportforetag) => item.namn?.toUpperCase().includes(val?.toUpperCase()) ?? false;

    uppdragsgivareSearchFn = (val: string, item: ICKLeverantor) => item.namn?.toUpperCase().includes(val?.toUpperCase()) ?? false;

    protected onSearch() {
        this.webinstallningarService.set<IExternArbetsorderSearchFilter>(this.webbInstallningarRecent, ObjectUtils.removeUndefinedProperties(this.model, true));
        const searchModel = _.cloneDeep(this.model);
        searchModel.transporttilldelad = this.formatService.stringToBoolean(searchModel.transporttilldeladFormValue);
        searchModel.transportavslutad = this.formatService.stringToBoolean(searchModel.transportavslutadFormValue);
        searchModel.grot = this.formatService.stringToBoolean(searchModel.grotFormValue);
        searchModel.isDirty = this.formatService.stringToBoolean(searchModel.isDirtyValue);
        searchModel.transportforetag = searchModel.transportforetag?.map(x => x.id);
        searchModel.uppdragsgivare = searchModel.uppdragsgivare?.map(x => x.id);
        this.externArbetsorderService.searchForExternArbetsorder(searchModel);
    }

    protected rensaSokFilter() {
        _.assign(this.model, _.cloneDeep(DEFAULT_MODEL));
        this.formBinderService.reBindForm(this.form, _.cloneDeep(this.model));
    }

    private getProduktionsomradenSelectionItems(produktionsomraden: IProduktionsomrade[]): ITreeSelectionItem[] {
        return _.chain(produktionsomraden)
            .groupBy(s => s.resultatomradeId)
            .map((resultatomraden, marknadsomradeId) => ({
                sekvensnummer: resultatomraden[0].sekvensnummer,
                text: resultatomraden[0].resultatomradeNamn,
                value: resultatomraden[0].id,
                children: _.orderBy(resultatomraden.map(s => (({
                    text: s.namn,
                    value: s.id
                }) as ITreeSelectionItem)), s => s.sekvensnummer)
            }))
            .orderBy(x => x.sekvensnummer)
            .value();
    }
}
